import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid, makeStyles } from "@material-ui/core";
import dayjs from "dayjs";
import * as React from "react";
import { useForm, Controller } from "react-hook-form";

import { MonthlyCashflowResult } from "@/api/generated";
import { CustomButton } from "@/components/Button";
import { PropertyEditErrorSummary } from "@/components/property_edit/PropertyEditErrorSummary";
import { CustomForm } from "@/components/property_edit/PropertyEditInputs";
import { CustomSelector } from "@/components/property_edit/PropertyEditInputs";
import theme from "@/theme";
import { parseFormattedNumber } from "@/utilities/format";
import { createLoanInfoHistorySchema, LoanInfoHistoryFormData } from "@/validations/management/edit";

import { useEditMonthlyCashflowResult } from "../../../hooks/useEditMonthlyCashflowResult";

const useStyles = makeStyles(() => ({
  background: {
    backgroundColor: theme.palette.background.default,
    borderRadius: 8,
    padding: 16,
  },
}));

interface props {
  monthlyCashflowResult?: MonthlyCashflowResult;
  onCancel: () => void;
  onSave?: () => void;
  onCreate?: () => void;
  propertyBoughtAt?: string;
}

const LoanPrepaymentMethodOptions = [
  { value: "", label: "未選択" },
  { value: "ReduceLoanAmount", label: "返済額軽減型" },
  { value: "ReduceLoanPaymentPeriod", label: "期間短縮型" },
];

// 履歴編集用コンポーネント
export const LoanInfoHistoryEditor: React.FC<props> = ({
  monthlyCashflowResult,
  onCancel,
  onSave,
  onCreate,
  propertyBoughtAt,
}) => {
  const classes = useStyles();

  const dynamicSchema = createLoanInfoHistorySchema(propertyBoughtAt);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<LoanInfoHistoryFormData>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: {
      month: dayjs(monthlyCashflowResult?.month).format("YYYY-MM") || "",
      loan_prepayment: monthlyCashflowResult?.loan_prepayment?.toString() || "",
      loan_prepayment_method: monthlyCashflowResult?.loan_prepayment_method || "",
    },
  });

  const { editMonthlyCashflowResult } = useEditMonthlyCashflowResult();

  const onSubmit = async (data: LoanInfoHistoryFormData) => {
    const originalMonth = monthlyCashflowResult?.month;
    const newMonth = `${data.month}-01`;

    // 削除対象のフィールドを特定
    const fieldsToDelete = ["loan_prepayment", "loan_prepayment_method"];

    // 新しいデータを準備
    const sendData = {
      ...data,
      month: newMonth,
      ...(data.loan_prepayment && {
        loan_prepayment: parseFormattedNumber(data.loan_prepayment),
      }),
    };

    // 履歴編集処理（削除→作成）
    await editMonthlyCashflowResult(fieldsToDelete, sendData, originalMonth);

    if (onSave) {
      onSave();
    } else if (onCreate) {
      onCreate();
    }
  };

  return (
    <Box className={classes.background} data-edit-form-panel>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box mb={2}>
          <Controller
            name="month"
            control={control}
            render={({ ...field }) => (
              <CustomForm
                {...field}
                label="支払開始月"
                required
                type="month"
                error={!!errors.month}
                errorText={errors.month?.message}
              />
            )}
          />
          <>
            <Controller
              name="loan_prepayment"
              control={control}
              render={({ ...field }) => (
                <CustomForm
                  {...field}
                  label="返済金額"
                  required
                  error={!!errors.loan_prepayment}
                  errorText={errors.loan_prepayment?.message}
                  placeHolder="100,000,000"
                  unitSuffix="円"
                  formatNumeric
                />
              )}
            />
            <Controller
              name="loan_prepayment_method"
              control={control}
              render={({ ...field }) => (
                <CustomSelector
                  {...field}
                  label="返済方式"
                  onChange={field.onChange}
                  error={!!errors.loan_prepayment_method}
                  errorText={errors.loan_prepayment_method?.message}
                  options={LoanPrepaymentMethodOptions}
                />
              )}
            />
          </>
        </Box>

        <PropertyEditErrorSummary errors={errors} />
        <Grid container spacing={2} data-edit-form-actions>
          <Grid item xs={6}>
            <CustomButton customVariant="textPrimary" onClick={onCancel} size="small" fullWidth>
              キャンセル
            </CustomButton>
          </Grid>
          <Grid item xs={6}>
            <CustomButton buttonSize="L" customVariant="filled" type="submit" size="small" fullWidth>
              保存
            </CustomButton>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
